import type { AxiosResponse } from 'axios';

/**
 * 解析 axios 返回值。注意：`services/api` 响应拦截器已返回「后端 JSON 本体」
 *（通常为 `{ code, message, data }`），此处不得再取 `.data`，否则会丢掉 `code`，
 * 前端误判为「API 异常」。
 */
export function getAxiosBody<T>(res: unknown): T | undefined {
  if (res == null || typeof res !== 'object') return undefined;
  const o = res as Record<string, unknown>;
  if (typeof o.code === 'number') {
    return res as T;
  }
  // 极少数路径仍返回 AxiosResponse（未走默认拦截器）
  const ax = res as AxiosResponse<T>;
  if (typeof ax?.status === 'number' && ax.data !== undefined) {
    return ax.data;
  }
  return res as T;
}

/** 常见封装 `{ code, data, msg }`，也可能直接是分页或数组 */
export function unwrapListBody<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === 'object') {
    const b = body as { code?: number; data?: unknown; records?: T[] };
    if (Array.isArray(b.records)) return b.records;
    const inner = b.data;
    if (Array.isArray(inner)) return inner as T[];
    if (inner && typeof inner === 'object' && 'records' in inner) {
      const rec = (inner as { records?: T[] }).records;
      return Array.isArray(rec) ? rec : [];
    }
  }
  return [];
}

/**
 * `api` 拦截器已返回后端 JSON 本体：`{ code, message, data }`，其中 data 为 MyBatis Page → `{ records, total, ... }`
 */
export function unwrapResultPage<T>(res: unknown): T[] {
  if (res == null || typeof res !== 'object') return [];
  const r = res as { code?: number; data?: { records?: T[] } };
  if (r.code !== undefined && r.code !== 200) return [];
  const page = r.data;
  if (page && typeof page === 'object' && Array.isArray(page.records)) return page.records as T[];
  return [];
}

/** 分页列表：同时取出 records 与 total（MyBatis Page） */
export function unwrapResultPageWithTotal<T>(res: unknown): { records: T[]; total: number } {
  if (res == null || typeof res !== 'object') return { records: [], total: 0 };
  const r = res as { code?: number; data?: { records?: T[]; total?: number } };
  if (r.code !== undefined && r.code !== 200) return { records: [], total: 0 };
  const page = r.data;
  if (page && typeof page === 'object' && Array.isArray(page.records)) {
    return { records: page.records as T[], total: Number(page.total ?? 0) };
  }
  return { records: [], total: 0 };
}

/** Result.success(T[]) 或 Result.success(数组在 data) */
export function unwrapResultArray<T>(res: unknown): T[] {
  if (res == null || typeof res !== 'object') return [];
  const r = res as { code?: number; data?: unknown };
  if (r.code !== undefined && r.code !== 200) return [];
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  return [];
}

/** Result.success(任意对象)，如 Map 序列化后的 Record */
export function unwrapResultData<T>(res: unknown): T | undefined {
  if (res == null || typeof res !== 'object') return undefined;
  const r = res as { code?: number; data?: unknown };
  if (r.code !== undefined && r.code !== 200) return undefined;
  const data = r.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const nested = data as { code?: number; data?: unknown };
    if (typeof nested.code === 'number' && Object.prototype.hasOwnProperty.call(nested, 'data')) {
      if (nested.code !== 200) return undefined;
      return nested.data as T;
    }
  }
  return data as T | undefined;
}

/** 后端 Result 非 200 时抛出，便于调用方 catch 展示 message */
export function assertResultOk(res: unknown, fallback = '请求失败'): void {
  if (res == null || typeof res !== 'object') return;
  const r = res as { code?: number; message?: string };
  if (r.code !== undefined && r.code !== 200) {
    throw new Error(r.message?.trim() || fallback);
  }
}
